import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, Link as MuiLink, MenuItem, Paper, Stack, Switch, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, Tabs, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Forum as ChatIcon, WarningAmber as WarningIcon } from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useIsAdmin, usePermissions } from '../../../contexts/PermissionsContext';
import { useChat } from '../../../contexts/ChatContext';
import { formatChatDate, formatChatTime, parseChatTimestamp } from '../../../utils/chatFormat';
import { ADMIN_CHANNEL_TYPES, channelTypeLabel } from '../../../utils/chatChannelTypes';
import ChatChannelSettingsDialog from '../../../components/chat/ChatChannelSettingsDialog';

const displayOnlyChip = { clickable: false, onClick: () => {} };

const formatDateTime = (value) => (value ? `${formatChatDate(value)}, ${formatChatTime(value)}` : 'No messages yet');

const EntityLink = ({ slug, basePath, children }) => (slug ? (
  <MuiLink component={RouterLink} to={`${basePath}/${slug}`} underline="hover" sx={{ wordBreak: 'break-word' }}>
    {children}
  </MuiLink>
) : children);

export const leagueStatus = (league, now = new Date()) => {
  const end = parseChatTimestamp(league.endDate) || (league.endDate ? new Date(league.endDate) : null);
  if (end && !Number.isNaN(end.getTime()) && end < now) return { label: 'Ended', color: 'default' };
  if (!league.isActive) return { label: 'Inactive', color: 'default' };
  return { label: 'Active', color: 'success' };
};

export const hasMissingChannels = (league) => Boolean(league.enableChat
  && (league.teamChannelCount < league.teamCount
    || !league.hasLeagueChannel
    || !league.hasLeagueAdminsChannel
    || (league.hasSparePool && !league.hasSparesChannel)));

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  ...ADMIN_CHANNEL_TYPES.map((value) => ({ value, label: channelTypeLabel(value) })),
];

const TYPE_CHIP_COLORS = { League: 'secondary', LeagueAdmins: 'primary', Spares: 'info' };

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

/**
 * Chat admin area for Super Admins, Tenant Admins, and League Managers (the server limits League
 * Managers to their own leagues). Turn chat on per league, then manage individual channels.
 */
const ChatAdminPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAdmin = useIsAdmin();
  const { isLoading: permissionsLoading, canManageAnyLeague } = usePermissions();
  const hasAccess = isAdmin || Boolean(canManageAnyLeague?.());
  const { refreshChannels } = useChat();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialLeagueId = searchParams.get('leagueId') || '';
  const [tab, setTab] = useState(initialLeagueId ? 'channels' : 'leagues');
  const [leagueFilter, setLeagueFilter] = useState(initialLeagueId);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [busyLeagueId, setBusyLeagueId] = useState(null);
  const [busyChannelId, setBusyChannelId] = useState(null);
  const [confirmDisable, setConfirmDisable] = useState(null);
  const [manageChannelId, setManageChannelId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadLeagues = useCallback(async () => {
    try {
      const data = await unifiedApi.getAdminChatLeagues();
      setLeagues(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load leagues.');
    } finally {
      setLeaguesLoading(false);
    }
  }, []);

  const loadChannels = useCallback(async () => {
    setChannelsLoading(true);
    try {
      const data = await unifiedApi.getAdminChatChannels({
        leagueId: leagueFilter || undefined,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
      });
      setChannels(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load chat channels.');
    } finally {
      setChannelsLoading(false);
    }
  }, [leagueFilter, typeFilter, statusFilter]);

  useEffect(() => {
    if (!hasAccess) return;
    loadLeagues();
  }, [hasAccess, loadLeagues]);

  useEffect(() => {
    if (!hasAccess || tab !== 'channels') return;
    loadChannels();
  }, [hasAccess, tab, loadChannels]);

  const changeLeagueFilter = (value) => {
    setLeagueFilter(value);
    const next = new URLSearchParams(searchParams);
    if (value) next.set('leagueId', value); else next.delete('leagueId');
    setSearchParams(next, { replace: true });
  };

  const viewChannels = (league) => {
    changeLeagueFilter(String(league.leagueId));
    setTypeFilter('');
    setStatusFilter('');
    setTab('channels');
  };

  const setLeagueChat = async (league, enableChat, successMessage) => {
    setBusyLeagueId(league.leagueId);
    setError(null);
    setSuccess(null);
    try {
      const updated = await unifiedApi.setLeagueChatEnabled(league.leagueId, enableChat);
      if (updated) {
        setLeagues((prev) => prev.map((l) => (l.leagueId === league.leagueId ? updated : l)));
      } else {
        await loadLeagues();
      }
      setSuccess(successMessage);
      refreshChannels?.();
    } catch (err) {
      setError(err.message || 'Failed to update league chat.');
    } finally {
      setBusyLeagueId(null);
    }
  };

  const handleLeagueToggle = (league) => {
    if (league.enableChat) {
      setConfirmDisable(league);
    } else {
      setLeagueChat(league, true, `Chat turned on for ${league.leagueName}.`);
    }
  };

  const confirmDisableChat = async () => {
    const league = confirmDisable;
    setConfirmDisable(null);
    if (league) await setLeagueChat(league, false, `Chat turned off for ${league.leagueName}.`);
  };

  const handleChannelToggle = async (channel) => {
    setBusyChannelId(channel.id);
    setError(null);
    setSuccess(null);
    try {
      await unifiedApi.updateChatChannel(channel.id, { isEnabled: !channel.isEnabled });
      setChannels((prev) => prev.map((c) => (c.id === channel.id ? { ...c, isEnabled: !c.isEnabled } : c)));
      refreshChannels?.();
    } catch (err) {
      setError(err.message || 'Failed to update chat channel.');
    } finally {
      setBusyChannelId(null);
    }
  };

  const handleSettingsSaved = () => {
    loadChannels();
    refreshChannels?.();
  };

  if (permissionsLoading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </AdminLayout>
    );
  }

  if (!hasAccess) {
    return (
      <AdminLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">You must be an organization admin or a league manager to manage chat.</Alert>
        </Box>
      </AdminLayout>
    );
  }

  const leagueChatSwitch = (league) => (
    <FormControlLabel
      control={(
        <Switch
          checked={Boolean(league.enableChat)}
          onChange={() => handleLeagueToggle(league)}
          disabled={busyLeagueId === league.leagueId}
          inputProps={{ 'aria-label': `Chat enabled for ${league.leagueName}` }}
        />
      )}
      label={league.enableChat ? 'On' : 'Off'}
    />
  );

  const missingChannels = (league) => hasMissingChannels(league) && (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
      <Chip icon={<WarningIcon />} label="Missing channels" color="warning" size="small" variant="outlined" {...displayOnlyChip} />
      <Button
        size="small"
        variant="outlined"
        onClick={() => setLeagueChat(league, true, `Missing channels created for ${league.leagueName}.`)}
        disabled={busyLeagueId === league.leagueId}
        fullWidth={isMobile}
      >
        Create missing channels
      </Button>
    </Stack>
  );

  const channelCounts = (league) => `${league.enabledChannelCount}/${league.channelCount} enabled`;

  const viewChannelsButton = (league) => (
    <Button
      size="small"
      onClick={() => viewChannels(league)}
      aria-label={`View channels for ${league.leagueName}`}
      fullWidth={isMobile}
    >
      View channels
    </Button>
  );

  const renderLeagues = () => {
    if (leaguesLoading) return <CircularProgress size={24} />;
    if (leagues.length === 0) return <Typography color="text.secondary">No leagues to manage.</Typography>;

    if (isMobile) {
      return (
        <Stack spacing={1.5}>
          {leagues.map((league) => {
            const status = leagueStatus(league);
            return (
              <Paper key={league.leagueId} variant="outlined" sx={{ p: 2 }} data-testid={`chat-league-card-${league.leagueId}`}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography fontWeight={600} sx={{ wordBreak: 'break-word', minWidth: 0 }}>
                    <EntityLink slug={league.leagueSlug} basePath="/leagues">{league.leagueName}</EntityLink>
                  </Typography>
                  <Chip label={status.label} color={status.color} size="small" {...displayOnlyChip} />
                </Stack>
                {leagueChatSwitch(league)}
                <Typography variant="body2">Channels: {channelCounts(league)}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Last activity: {formatDateTime(league.lastMessageAt)}
                </Typography>
                {missingChannels(league)}
                <Box sx={{ mt: 1 }}>{viewChannelsButton(league)}</Box>
              </Paper>
            );
          })}
        </Stack>
      );
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>League</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Chat</TableCell>
            <TableCell>Channels</TableCell>
            <TableCell>Last activity</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {leagues.map((league) => {
            const status = leagueStatus(league);
            return (
              <TableRow key={league.leagueId}>
                <TableCell>
                  <EntityLink slug={league.leagueSlug} basePath="/leagues">{league.leagueName}</EntityLink>
                </TableCell>
                <TableCell><Chip label={status.label} color={status.color} size="small" {...displayOnlyChip} /></TableCell>
                <TableCell>{leagueChatSwitch(league)}</TableCell>
                <TableCell>
                  {channelCounts(league)}
                  {missingChannels(league)}
                </TableCell>
                <TableCell>{formatDateTime(league.lastMessageAt)}</TableCell>
                <TableCell align="right">{viewChannelsButton(league)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const channelEnabledSwitch = (channel) => (
    <FormControlLabel
      control={(
        <Switch
          checked={Boolean(channel.isEnabled)}
          onChange={() => handleChannelToggle(channel)}
          disabled={busyChannelId === channel.id}
          inputProps={{ 'aria-label': `Channel enabled for ${channel.name}` }}
        />
      )}
      label={channel.isEnabled ? 'Enabled' : 'Disabled'}
    />
  );

  const manageButton = (channel) => (
    <Button
      size="small"
      variant="outlined"
      onClick={() => setManageChannelId(channel.id)}
      aria-label={`Manage ${channel.name}`}
      fullWidth={isMobile}
    >
      Manage
    </Button>
  );

  const typeChip = (channel) => (
    <Chip
      label={channelTypeLabel(channel.channelType)}
      color={TYPE_CHIP_COLORS[channel.channelType] || 'default'}
      size="small"
      {...displayOnlyChip}
    />
  );

  const channelName = (channel) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography fontWeight={600} sx={{ wordBreak: 'break-word' }}>{channel.name}</Typography>
      {channel.channelType === 'Team' && channel.teamName && (
        <Typography variant="caption" color="text.secondary" display="block">
          Team: <EntityLink slug={channel.teamSlug} basePath="/teams">{channel.teamName}</EntityLink>
        </Typography>
      )}
      {!channel.leagueChatEnabled && (
        <Chip
          icon={<WarningIcon />}
          label="Hidden — chat is off for this league"
          color="warning"
          size="small"
          variant="outlined"
          sx={{ mt: 0.5, maxWidth: '100%' }}
          {...displayOnlyChip}
        />
      )}
    </Box>
  );

  const renderChannels = () => {
    if (channelsLoading && channels.length === 0) return <CircularProgress size={24} />;
    if (channels.length === 0) return <Typography color="text.secondary">No chat channels match these filters.</Typography>;

    if (isMobile) {
      return (
        <Stack spacing={1.5}>
          {channels.map((channel) => (
            <Paper key={channel.id} variant="outlined" sx={{ p: 2 }} data-testid={`chat-channel-card-${channel.id}`}>
              <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                {channelName(channel)}
                {typeChip(channel)}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                <EntityLink slug={channel.leagueSlug} basePath="/leagues">{channel.leagueName}</EntityLink>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {channel.memberCount} members · {channel.adminCount} admins · {channel.messageCount} messages
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Last message: {formatDateTime(channel.lastMessageAt)}
              </Typography>
              {channelEnabledSwitch(channel)}
              <Box sx={{ mt: 1 }}>{manageButton(channel)}</Box>
            </Paper>
          ))}
        </Stack>
      );
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Channel</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>League</TableCell>
            <TableCell align="right">Members</TableCell>
            <TableCell align="right">Admins</TableCell>
            <TableCell align="right">Messages</TableCell>
            <TableCell>Last message</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {channels.map((channel) => (
            <TableRow key={channel.id}>
              <TableCell>{channelName(channel)}</TableCell>
              <TableCell>{typeChip(channel)}</TableCell>
              <TableCell>
                <EntityLink slug={channel.leagueSlug} basePath="/leagues">{channel.leagueName}</EntityLink>
              </TableCell>
              <TableCell align="right">{channel.memberCount}</TableCell>
              <TableCell align="right">{channel.adminCount}</TableCell>
              <TableCell align="right">{channel.messageCount}</TableCell>
              <TableCell>{formatDateTime(channel.lastMessageAt)}</TableCell>
              <TableCell>{channelEnabledSwitch(channel)}</TableCell>
              <TableCell align="right">{manageButton(channel)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const leagueFilterValue = leagues.some((l) => String(l.leagueId) === leagueFilter) ? leagueFilter : '';

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <ChatIcon color="primary" />
          <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Chat Admin
          </Typography>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          Turning chat on for a league creates a league-wide channel for everyone in the league and a channel for
          each team. Turning it off hides the league&apos;s channels from members but keeps their messages. Messages
          are deleted automatically after 90 days.
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <Paper sx={{ mb: 2 }}>
          <Tabs value={tab} onChange={(_event, value) => setTab(value)} variant="fullWidth" aria-label="Chat admin sections">
            <Tab value="leagues" label="Leagues" />
            <Tab value="channels" label="Channels" />
          </Tabs>
        </Paper>

        {tab === 'leagues' && (
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>{renderLeagues()}</Paper>
        )}

        {tab === 'channels' && (
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <TextField
                select
                label="League"
                value={leagueFilterValue}
                onChange={(event) => changeLeagueFilter(event.target.value)}
                size="small"
                sx={{ minWidth: { xs: '100%', md: 260 } }}
              >
                <MenuItem value="">All leagues</MenuItem>
                {leagues.map((league) => (
                  <MenuItem key={league.leagueId} value={String(league.leagueId)}>{league.leagueName}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Type"
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                size="small"
                sx={{ minWidth: { xs: '100%', md: 180 } }}
              >
                {TYPE_OPTIONS.map((o) => <MenuItem key={o.label} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                size="small"
                sx={{ minWidth: { xs: '100%', md: 180 } }}
              >
                {STATUS_OPTIONS.map((o) => <MenuItem key={o.label} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Stack>
            {renderChannels()}
          </Paper>
        )}
      </Box>

      <Dialog open={Boolean(confirmDisable)} onClose={() => setConfirmDisable(null)}>
        <DialogTitle>Turn off chat?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`All chat channels in ${confirmDisable?.leagueName || 'this league'} will be hidden from members. `}
            Nothing is deleted — turning chat back on restores the channels and their messages.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisable(null)}>Cancel</Button>
          <Button onClick={confirmDisableChat} color="warning" variant="contained">Turn off chat</Button>
        </DialogActions>
      </Dialog>

      <ChatChannelSettingsDialog
        open={Boolean(manageChannelId)}
        channelId={manageChannelId}
        onClose={() => setManageChannelId(null)}
        onSaved={handleSettingsSaved}
      />
    </AdminLayout>
  );
};

export default ChatAdminPage;
